class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.mode = this.dataset.cartNotificationMode || 'notification';
    this.isModalType = this.mode === 'modal';
    this.notification = document.getElementById('cart-notification');
    this.backdrop = this.querySelector('.cart-notification__backdrop');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.onResize = this.positionModal.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open(anchorElement = this.activeElement) {
    this.activeElement = anchorElement || this.activeElement;
    this.notification.classList.remove('cart-notification--error');
    this.notification.classList.add('animate', 'active');

    if (this.isModalType) {
      if (this.backdrop) this.backdrop.hidden = false;
      this.positionModal();
      window.addEventListener('resize', this.onResize);
      window.addEventListener('scroll', this.onResize, true);
    }

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');
    this.notification.classList.remove('cart-notification--error');
    document.body.removeEventListener('click', this.onBodyClick);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('scroll', this.onResize, true);
    if (this.backdrop) this.backdrop.hidden = true;

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState, anchorElement = this.activeElement) {
    this.cartItemKey = parsedState.key;
    // 修正: モーダル表示中の追加（モーダル内の「カートに入れる」）では、スライダーの位置を保ち、追加した商品名を読み上げる。
    const isAddedInModal = this.isModalType && this.notification.classList.contains('active');
    // 修正: 商品詳細ページのモーダル内追加では、表示中のおすすめ（直前に表示した明細）を維持する。
    // それ以外（「一緒に購入」エリア・メインのカートボタン・他ページ）は追加した明細のおすすめを表示する。
    const crossSellHTML = parsedState.sections && parsedState.sections['cart-notification-cross-sell'];
    const keepsCrossSellSource =
      isAddedInModal &&
      document.body.classList.contains('template-product') &&
      Boolean(this.crossSellSourceKey) &&
      Boolean(crossSellHTML) &&
      crossSellHTML.includes(`id="cart-notification-cross-sell-${this.crossSellSourceKey}"`);
    if (!keepsCrossSellSource) this.crossSellSourceKey = parsedState.key;
    // 連続追加やセクション取得失敗時に前の商品のおすすめを残さない。
    const crossSell = this.querySelector('#cart-notification-cross-sell');
    if (crossSell) {
      crossSell.innerHTML = '';
      crossSell.hidden = true;
    }
    this.getSectionsToRender().forEach((section) => {
      const sectionContainer = document.getElementById(section.id);
      if (!sectionContainer || !parsedState.sections || !parsedState.sections[section.id]) return;
      sectionContainer.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    if (crossSell) crossSell.hidden = !crossSell.querySelector('.p-cart-modal__card');
    // 差し替えたおすすめ商品のスライダーを初期化（位置計算より前に実行する）。
    if (crossSell)
      this.dispatchEvent(
        new CustomEvent('cart-notification:cross-sell-rendered', {
          bubbles: true,
          detail: { keepPosition: isAddedInModal },
        })
      );
    const crossSellStatus = this.querySelector('#cart-notification-cross-sell-status');
    if (crossSellStatus) crossSellStatus.textContent = isAddedInModal ? `${parsedState.product_title}をカートに追加しました` : '';

    if (this.header) this.header.reveal();
    this.open(anchorElement);
  }

  renderError(anchorElement = this.activeElement) {
    this.notification.classList.add('cart-notification--error');
    this.notification.classList.add('animate', 'active');

    if (this.isModalType) {
      this.activeElement = anchorElement || this.activeElement;
      if (this.backdrop) this.backdrop.hidden = false;
      this.positionModal();
      window.addEventListener('resize', this.onResize);
      window.addEventListener('scroll', this.onResize, true);
    }

    this.notification.focus();
    trapFocus(this.notification);
    document.body.addEventListener('click', this.onBodyClick);
  }

  getSectionsToRender() {
    if (this.isModalType) {
      return [
        {
          id: 'cart-notification-cross-sell',
          // 修正: renderContents で決めた明細（crossSellSourceKey）のおすすめを使う
          selector: `[id="cart-notification-cross-sell-${this.crossSellSourceKey || this.cartItemKey}"]`,
        },
        {
          id: 'cart-icon-bubble',
        },
      ];
    }

    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    // 対象明細が返らない場合も追加完了通知とカート導線を維持する。
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || '';
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (this.backdrop && target === this.backdrop) {
      this.close();
      return;
    }
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  positionModal() {
    if (!this.isModalType) return;

    // 商品詳細・カートページとも、追加ボタンの位置に依存せず画面中央へ表示する。
    const notificationRect = this.notification.getBoundingClientRect();
    const viewportGap = 8;
    const left = Math.max((window.innerWidth - notificationRect.width) / 2, viewportGap);
    const top = Math.max((window.innerHeight - notificationRect.height) / 2, viewportGap);

    this.notification.style.left = `${left}px`;
    this.notification.style.top = `${top}px`;
  }
}

customElements.define('cart-notification', CartNotification);
